import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { X, Calendar, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { base44 } from "@/api/base44Client";
import { toast } from "sonner";
import { format, addDays, startOfWeek, endOfWeek, isSameDay, parseISO, addWeeks, subWeeks } from "date-fns";
import { nb } from "date-fns/locale";
import { cn } from "@/lib/utils";

export default function ScheduleMeetingDialog({ candidateId, candidateName, onClose }) {
  const [loading, setLoading] = useState(false);
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [formData, setFormData] = useState({
    title: `Statusmøte: ${candidateName}`,
    description: `Møte om oppkjøpsstatus for ${candidateName}`,
    date: "",
    startTime: "10:00",
    endTime: "11:00",
    attendees: "",
  });

  useEffect(() => {
    loadCalendarEvents();
  }, [currentWeek]);

  const loadCalendarEvents = async () => {
    setLoadingCalendar(true);
    try {
      const weekStart = startOfWeek(currentWeek, { locale: nb });
      const weekEnd = endOfWeek(currentWeek, { locale: nb });

      const response = await base44.functions.invoke('getCalendarAvailability', {
        startDate: weekStart.toISOString(),
        endDate: weekEnd.toISOString(),
      });

      if (response.data.success) {
        setCalendarEvents(response.data.events);
      }
    } catch (error) {
      console.error('Error loading calendar:', error);
    } finally {
      setLoadingCalendar(false);
    }
  };

  const getEventsForDateTime = (date, hour) => {
    return calendarEvents.filter(event => {
      if (!event.start?.dateTime) return false;
      const eventStart = parseISO(event.start.dateTime);
      const eventEnd = parseISO(event.end.dateTime);
      const slotTime = new Date(date);
      slotTime.setHours(hour, 0, 0, 0);
      return eventStart <= slotTime && eventEnd > slotTime;
    });
  };

  const handleTimeSlotClick = (date, hour) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const timeStr = `${hour.toString().padStart(2, '0')}:00`;
    const endHour = hour + 1;
    const endTimeStr = `${endHour.toString().padStart(2, '0')}:00`;
    
    setFormData({
      ...formData,
      date: dateStr,
      startTime: timeStr,
      endTime: endTimeStr,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const startDateTime = new Date(`${formData.date}T${formData.startTime}:00`).toISOString();
      const endDateTime = new Date(`${formData.date}T${formData.endTime}:00`).toISOString();

      const attendeesList = formData.attendees
        .split(',')
        .map(email => email.trim())
        .filter(email => email)
        .map(email => ({ email }));

      const response = await base44.functions.invoke('scheduleAcquisitionMeeting', {
        candidateId,
        candidateName,
        title: formData.title,
        description: formData.description,
        startTime: startDateTime,
        endTime: endDateTime,
        attendees: attendeesList,
      });

      if (response.data.success) {
        toast.success('Møte opprettet i Google Calendar');
        onClose();
      } else {
        toast.error('Kunne ikke opprette møte');
      }
    } catch (error) {
      console.error('Error scheduling meeting:', error);
      toast.error('Feil ved oppretting av møte');
    } finally {
      setLoading(false);
    }
  };

  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const day = addDays(startOfWeek(currentWeek, { locale: nb }), i);
    return day;
  });

  const workHours = Array.from({ length: 10 }, (_, i) => i + 8); // 8:00 - 17:00

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-blue-600" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900">
              Planlegg Møte
            </h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Calendar View */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-base">Velg Tid fra Kalender</Label>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => setCurrentWeek(subWeeks(currentWeek, 1))}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm font-medium text-slate-700 min-w-[150px] text-center">
                  Uke {format(currentWeek, 'w, yyyy', { locale: nb })}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => setCurrentWeek(addWeeks(currentWeek, 1))}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {loadingCalendar ? (
              <div className="h-64 flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : (
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="grid grid-cols-8 bg-slate-50">
                  <div className="p-2 text-xs font-medium text-slate-500 border-r border-slate-200">Tid</div>
                  {weekDays.map((day, i) => (
                    <div
                      key={i}
                      className={cn(
                        "p-2 text-xs font-medium text-center border-r border-slate-200 last:border-r-0",
                        isSameDay(day, new Date()) ? "bg-blue-50 text-blue-700" : "text-slate-700"
                      )}
                    >
                      <div>{format(day, 'EEE', { locale: nb })}</div>
                      <div className="text-sm">{format(day, 'd MMM', { locale: nb })}</div>
                    </div>
                  ))}
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {workHours.map((hour) => (
                    <div key={hour} className="grid grid-cols-8 border-t border-slate-100">
                      <div className="p-2 text-xs text-slate-500 border-r border-slate-200 flex items-center">
                        {hour}:00
                      </div>
                      {weekDays.map((day, i) => {
                        const events = getEventsForDateTime(day, hour);
                        const isBooked = events.length > 0;
                        const isPast = new Date(day.setHours(hour, 0, 0, 0)) < new Date();
                        const isSelected = formData.date === format(day, 'yyyy-MM-dd') && 
                                          formData.startTime === `${hour.toString().padStart(2, '0')}:00`;

                        return (
                          <button
                            key={i}
                            type="button"
                            disabled={isBooked || isPast}
                            onClick={() => handleTimeSlotClick(day, hour)}
                            className={cn(
                              "p-2 text-xs border-r border-slate-200 last:border-r-0 min-h-[50px] transition-colors",
                              isBooked && "bg-red-50 cursor-not-allowed",
                              isPast && !isBooked && "bg-slate-50 cursor-not-allowed text-slate-300",
                              !isBooked && !isPast && "hover:bg-blue-50 cursor-pointer",
                              isSelected && "bg-blue-100 ring-2 ring-blue-500 ring-inset"
                            )}
                          >
                            {isBooked ? (
                              <div className="text-red-600 font-medium truncate" title={events[0].summary}>
                                Opptatt
                              </div>
                            ) : isPast ? (
                              <div className="text-slate-400">-</div>
                            ) : (
                              <div className="text-emerald-600">Ledig</div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="pt-4 border-t border-slate-200 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Møtetittel *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>

          <div className="space-y-2">
            <Label htmlFor="description">Beskrivelse</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="date">Dato *</Label>
              <Input
                id="date"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="startTime">Start Tid *</Label>
              <Input
                id="startTime"
                type="time"
                value={formData.startTime}
                onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="endTime">Slutt Tid *</Label>
              <Input
                id="endTime"
                type="time"
                value={formData.endTime}
                onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="attendees">Deltakere (e-post)</Label>
            <Input
              id="attendees"
              placeholder="person1@example.com, person2@example.com"
              value={formData.attendees}
              onChange={(e) => setFormData({ ...formData, attendees: e.target.value })}
            />
            <p className="text-xs text-slate-500">Skill e-postadresser med komma</p>
          </div>
          
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <Button type="button" variant="outline" onClick={onClose}>
              Avbryt
            </Button>
            <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700">
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Oppretter...
                </>
              ) : (
                <>
                  <Calendar className="w-4 h-4 mr-2" />
                  Opprett Møte
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}